/* Timeline - TT — each moment is a card in a deck.
   Next: the top card is flung off to the left and the one beneath rises into place.
   Previous: the last card flies back in on top. The states live in CSS classes
   (is-past / is-active / is-next-1 / is-next-2 / is-later); this only assigns them. */
(() => {
  if (customElements.get('tt-timeline')) return;

  class TTTimeline extends HTMLElement {
    connectedCallback() {
      this.tiles = Array.from(this.querySelectorAll('[data-tile]'));
      this.moments = Array.from(this.querySelectorAll('[data-moment]'));
      this.deck = this.querySelector('[data-deck]');
      this.rail = this.querySelector('[data-rail]');
      this.strip = this.querySelector('[data-strip]');
      this.countEl = this.querySelector('[data-count]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      if (!this.tiles.length) return;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.bindSwipe();

      this.active = 0;
      this.render(false);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onClick(event) {
      const tile = event.target.closest('[data-tile]');
      if (tile) return this.select(Number(tile.dataset.index));
      if (event.target.closest('[data-prev]')) return this.select(this.active - 1);
      if (event.target.closest('[data-next]')) return this.select(this.active + 1);
    }

    /* tabs pattern: arrow keys move between moments */
    onKeydown(event) {
      if (!event.target.closest('[data-tile]')) return;
      const moves = {
        ArrowLeft: this.active - 1,
        ArrowRight: this.active + 1,
        Home: 0,
        End: this.tiles.length - 1,
      };
      if (!(event.key in moves)) return;
      event.preventDefault();
      this.select(moves[event.key]);
      this.tiles[this.active].focus();
    }

    onBlockSelect(event) {
      const tile = event.target.closest && event.target.closest('[data-tile]');
      const i = this.tiles.indexOf(tile);
      if (i > -1) this.select(i);
    }

    bindSwipe() {
      if (!this.deck) return;
      let startX = 0;
      let startY = 0;

      this.deck.addEventListener(
        'touchstart',
        (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.deck.addEventListener(
        'touchend',
        (event) => {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
          this.select(this.active + (dx < 0 ? 1 : -1));
        },
        { passive: true }
      );
    }

    select(i) {
      const index = Math.max(0, Math.min(i, this.tiles.length - 1));
      if (index === this.active) return;
      this.active = index;
      this.render(true);
    }

    render(animate) {
      const index = this.active;
      const states = ['is-past', 'is-active', 'is-next-1', 'is-next-2', 'is-later'];

      this.tiles.forEach((tile, k) => {
        const on = k === index;
        tile.setAttribute('aria-selected', on ? 'true' : 'false');
        tile.tabIndex = on ? 0 : -1;
        tile.classList.toggle('is-past', k < index);
      });

      this.moments.forEach((card, k) => {
        const offset = k - index;
        let state = 'is-later';
        if (offset < 0) state = 'is-past';
        else if (offset === 0) state = 'is-active';
        else if (offset === 1) state = 'is-next-1';
        else if (offset === 2) state = 'is-next-2';
        card.classList.remove(...states);
        card.classList.add(state);
        card.setAttribute('aria-hidden', offset === 0 ? 'false' : 'true');
      });

      const progress = this.tiles.length > 1 ? index / (this.tiles.length - 1) : 0;
      if (this.rail) this.rail.style.setProperty('--tt-tl-progress', progress);
      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.tiles.length - 1;
      if (this.countEl) this.countEl.textContent = `${index + 1} / ${this.tiles.length}`;

      /* keep the chosen tile in view when the strip scrolls sideways */
      if (this.strip && this.strip.scrollWidth > this.strip.clientWidth + 1) {
        const tile = this.tiles[index];
        const left = tile.offsetLeft - (this.strip.clientWidth - tile.offsetWidth) / 2;
        this.strip.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
      }
    }
  }

  customElements.define('tt-timeline', TTTimeline);
})();
